import { MinimumStationsGroup } from '@app/core/types/stations';
import {
  stationsStationsGroupsListQueryKey,
  useStationsStationsGroupsList,
} from '@mi-front/api-v2';
import { UserRole } from '@mi-front/core';
import { useMemo } from 'react';
import { useHasRole } from '../../auth/authorization/hooks/useHasRole';

/**
 * React Query hook for station groups via api-v2 generated query.
 * Cache is primed from app-init in AuthBootstrap using the api-v2 query key.
 * Superadmin has groups disabled and uses empty array.
 *
 * OpenAPI for `/station-groups/v1` is incomplete; runtime payload matches `WebAppStationGroupDto`.
 */
export function useStationsGroupsQuery() {
  const isSuperadmin = useHasRole(UserRole.SUPERADMIN);

  return useStationsStationsGroupsList<MinimumStationsGroup[]>({
    query: {
      enabled: !isSuperadmin,
      initialData: isSuperadmin ? [] : undefined,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      select: (data) => data as unknown as MinimumStationsGroup[],
    },
  });
}

useStationsGroupsQuery.queryKey = stationsStationsGroupsListQueryKey;

/**
 * Returns station groups from the query. Empty array when loading or superadmin.
 */
export function useStationsGroups(): MinimumStationsGroup[] {
  const query = useStationsGroupsQuery();

  return useMemo(() => query.data ?? [], [query.data]);
}
